/**
 * An invite to an email that already has an EV Initiative account (Warren, 29 October 2026):
 *
 *   "when an invite's email already has an EVI account, accepting it asks them to sign in and adds the new role to
 *    that account, never 'email taken'."
 *
 * The invite pages (Sales Partner, building staff) say his sentence and offer a sign-in as that account: its
 * password, a one-time sign-in link to its own mailbox, or Google. The Api checks it (apps/api lib/inviteSignIn.ts);
 * the link alone, emailed or copied, never accepts for an existing account. This file holds the words and the small
 * pieces of state the pages share; components/ui/InviteSignInPanel.tsx is the panel.
 */

export type InviteReturnKind = 'sales_partner' | 'building_staff';

/** Warren's sentence, word for word as the Api and the invite emails say it. */
export function existingAccountLine(roleLabel: string): string {
  return `You already have an EV Initiative account with this email. Sign in to add ${roleLabel} to it.`;
}

/** Signed in as someone else: the Api refuses the accept with the same words (INVITE_OTHER_ACCOUNT). */
export function otherAccountLine(email: string): string {
  return `You are signed in to EV Initiative with a different account, and this invite is for ${email}. Sign out, then open the invite link again.`;
}

export const INVITE_SIGN_IN_COPY = {
  email: 'Email',
  password: 'Password',
  signIn: 'Sign in and accept',
  signingIn: 'Signing in...',
  orLine: 'Or sign in without a password',
  emailLink: 'Email me a sign-in link',
  sendingLink: 'Sending...',
  google: 'Continue with Google',
  /** Holds {email}. */
  linkSent: 'We sent a sign-in link to {email}. Open it on this device to finish accepting.',
  linkFailed: 'The sign-in link could not be sent. Try again, or sign in with your password.',
  acceptSignedIn: 'Accept the invite',
  /** Holds {email}. */
  signedInAs: 'You are signed in as {email}.',
} as const;

/** The Sales Partner role, as the Api names it (SALES_PARTNER_ROLE_LABEL). */
export const SALES_PARTNER_ROLE_LABEL = 'Sales Partner access';
/** The building staff invite's role, as the Api names it (buildingInviteRoleLabel). */
export const buildingInviteRoleLabel = (locationName: string) => `the chargers at ${locationName || 'the building'}`;

const STASH_KEY = 'evi.invite_return';

/** Keep the invite token for Google's return (the token never rides the OAuth state; the page keeps it). */
export function stashInviteReturn(kind: InviteReturnKind, token: string, storage: Pick<Storage, 'setItem'> | null = safeSession()): void {
  try { storage?.setItem(STASH_KEY, JSON.stringify({ kind, token })); } catch { /* private mode: Google returns without it */ }
}

/** The token kept for this page, taken once (it is removed). */
export function takeInviteReturn(kind: InviteReturnKind, storage: Pick<Storage, 'getItem' | 'removeItem'> | null = safeSession()): string | null {
  try {
    const raw = storage?.getItem(STASH_KEY);
    if (!raw) return null;
    storage?.removeItem(STASH_KEY);
    const parsed = JSON.parse(raw) as { kind?: unknown; token?: unknown };
    return parsed.kind === kind && typeof parsed.token === 'string' && parsed.token ? parsed.token : null;
  } catch { return null; }
}

function safeSession(): Storage | null {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch { return null; }
}

/**
 * The page's URL on arrival: the invite token (from the URL, else kept for Google's return) and the one-time
 * sign-in token (?sign_in=, from the emailed sign-in link or Google). `rest` is the query to put back without
 * sign_in, so a refresh or a shared screenshot never carries it.
 */
export function readInvitePageUrl(search: string): { token: string; signIn: string | null; rest: string } {
  const params = new URLSearchParams(search);
  const token = (params.get('token') ?? '').trim();
  const signIn = (params.get('sign_in') ?? '').trim();
  params.delete('sign_in');
  params.delete('notice');
  return { token, signIn: /^[A-Za-z0-9_-]{32,128}$/.test(signIn) ? signIn : null, rest: params.toString() };
}

/** Google's start for an invite page: the Admin surface, and only the page's name in the signed state. */
export function googleInviteUrl(apiBase: string, kind: InviteReturnKind): string {
  return `${apiBase}/auth/google?surface=admin&invite_return=${kind}`;
}
